/**
 * NotificationCenter - Centre de notifications unifié
 * 
 * UN SEUL composant pour TOUTES les notifications en temps réel.
 * Remplace les multiples listeners (chat, email, etc.)
 * 
 * Architecture:
 * - 1 requête toutes les 30 secondes vers /api/admin/notifications/poll
 * - Dispatch selon le type de notification
 * - Son + popup personnalisés par type
 */
import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Eye, Mail, MessageSquare, Calendar, CheckSquare, Bell, FileCheck } from 'lucide-react';
import { useAuth } from '../Auth/AuthContext';

const NotificationCenter = () => {
  const { user } = useAuth();
  const { t } = useTranslation('admin');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const pollingRef = useRef(null);
  // Initialiser lastCheck à 5 minutes dans le passé pour ne pas manquer les notifications récentes
  const lastCheckRef = useRef(new Date(Date.now() - 5 * 60 * 1000).toISOString());
  const notifiedIdsRef = useRef(new Set()); // Évite les doublons
  
  // Ne pas activer si pas connecté ou pas admin/super_admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const getToken = () => {
    return sessionStorage.getItem('impersonate_token') || localStorage.getItem('token');
  };

  // ============================================================
  // SONS PAR TYPE DE NOTIFICATION
  // ============================================================
  const playSound = useCallback((type) => {
    // Vérifier les préférences utilisateur
    let soundType = 'toc_toc_porte'; // Son par défaut
    let enabled = true;
    
    switch (type) {
      case 'read_receipt':
        enabled = user?.read_receipt_sound_enabled !== false;
        soundType = user?.notification_sound_read_receipt || 'toc_toc_porte';
        break;
      case 'new_email':
        enabled = user?.email_sound_enabled !== false;
        soundType = user?.notification_sound_email || 'cell_message_2';
        break;
      case 'chat':
        enabled = user?.chat_sound_enabled !== false;
        soundType = user?.notification_sound_chat || 'cell_message_1';
        break;
      case 'appointment':
        enabled = user?.appointment_sound_enabled !== false;
        soundType = user?.notification_sound_appointment || 'cloche_eglise';
        break;
      case 'reminder':
        enabled = user?.reminder_sound_enabled !== false;
        soundType = user?.notification_sound_reminder || 'messenger';
        break;
      default:
        soundType = user?.notification_sound || 'toc_toc_porte';
    }
    
    if (!enabled || soundType === 'none') return;
    
    try {
      const extension = ['interference', 'messenger', 'ouin-ouin'].includes(soundType) ? 'mp3' : 'wav';
      const audio = new Audio(`/sounds/${soundType}.${extension}`);
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {
      console.error('[NotificationCenter] Erreur son:', e);
    }
  }, [user]);

  // ============================================================
  // AFFICHAGE DES POPUPS PAR TYPE
  // ============================================================
  
  // Popup pour accusé de réception
  const showReadReceiptPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
          border: '2px solid #334155',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=hub-com&subtab=mail&folder=sent';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#22C55E',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Eye size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#F1F5F9', fontSize: '14px' }}>
            {t('notif.toast.emailRead')}
          </div>
          <div style={{ color: '#94A3B8', fontSize: '13px', marginTop: '2px' }}>
            {data.to_addresses?.[0] || t('notif.toast.recipient')}
          </div>
          <div style={{ color: '#64748B', fontSize: '12px', marginTop: '2px' }}>
            {data.subject?.substring(0, 40)}{data.subject?.length > 40 ? '...' : ''}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour message chat (Jaune/Orange)
  const showChatPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
          border: '2px solid #F59E0B',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=chat-admin';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#F59E0B',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <MessageSquare size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#92400E', fontSize: '14px' }}>
            {t('notif.toast.chatNew')}
          </div>
          <div style={{ color: '#B45309', fontSize: '13px', marginTop: '2px' }}>
            {t('notif.toast.room')}{data.salon_name}
          </div>
          <div style={{ color: '#92400E', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {data.last_message?.substring(0, 40)}{data.last_message?.length > 40 ? '...' : ''}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour nouvel email (Bleu)
  const showNewEmailPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
          border: '2px solid #3B82F6',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=hub-com&subtab=mail&folder=inbox';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#3B82F6',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Mail size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#1E40AF', fontSize: '14px' }}>
            {t('notif.toast.emailNew')}
          </div>
          <div style={{ color: '#2563EB', fontSize: '13px', marginTop: '2px' }}>
            {data.from_name || data.from_address}
          </div>
          <div style={{ color: '#1E40AF', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {data.subject?.substring(0, 40)}{data.subject?.length > 40 ? '...' : ''}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour Hub Message (Violet)
  const showHubMessagePopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
          border: '2px solid #8B5CF6',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=hub-com';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#8B5CF6',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <MessageSquare size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#5B21B6', fontSize: '14px' }}>
            {t('notif.toast.hubNew')}
          </div>
          <div style={{ color: '#7C3AED', fontSize: '13px', marginTop: '2px' }}>
            {data.sender_name || t('notif.toast.sender')}
          </div>
          <div style={{ color: '#5B21B6', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {data.subject?.substring(0, 40)}{data.subject?.length > 40 ? '...' : ''}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour Rappel RDV (Vert)
  const showAppointmentPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
          border: '2px solid #10B981',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=appointments';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#10B981',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Calendar size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#065F46', fontSize: '14px' }}>
            {t('notif.toast.apptReminder')}
          </div>
          <div style={{ color: '#047857', fontSize: '13px', marginTop: '2px' }}>
            {data.title}
          </div>
          <div style={{ color: '#065F46', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {data.client_name || t('notif.toast.inFewMin')}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour Rappel Agenda (Cyan)
  const showReminderPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)',
          border: '2px solid #06B6D4',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(6, 182, 212, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=appointments';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#06B6D4',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Bell size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#155E75', fontSize: '14px' }}>
            {t('notif.toast.agendaReminder')}
          </div>
          <div style={{ color: '#0891B2', fontSize: '13px', marginTop: '2px' }}>
            {data.title}
          </div>
          <div style={{ color: '#155E75', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {data.description?.substring(0, 40) || t('notif.toast.inFewMin')}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour Tâche (Rouge)
  const showTaskPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
          border: '2px solid #EF4444',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(239, 68, 68, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=appointments';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#EF4444',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CheckSquare size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#991B1B', fontSize: '14px' }}>
            {t('notif.toast.taskDue')}
          </div>
          <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '2px' }}>
            {data.title}
          </div>
          <div style={{ color: '#991B1B', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {t('notif.toast.priority')}{data.priority || t('notif.toast.normal')}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour Devis accepté (Or/Doré)
  const showQuoteAcceptedPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div 
        style={{
          background: 'linear-gradient(135deg, #FEF9C3 0%, #FDE047 100%)',
          border: '2px solid #EAB308',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(234, 179, 8, 0.4)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => {
          window.location.href = '/admin?tab=hub-etude';
          toast.dismiss(toastId);
        }}
      >
        <div style={{
          background: '#EAB308',
          borderRadius: '50%',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <FileCheck size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#854D0E', fontSize: '14px' }}>
            {t('notif.toast.quoteAccepted')}
          </div>
          <div style={{ color: '#A16207', fontSize: '13px', marginTop: '2px' }}>
            {data.client_name || data.reference}
          </div>
          <div style={{ color: '#854D0E', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>
            {data.total_amount ? `${data.total_amount}€` : data.reference}
          </div>
        </div>
      </div>
    ), {
      duration: 15000,
      position: 'top-right'
    });
  }, []);

  // Popup pour Demande de clôture d'assistance (Rose)
  const showClosureRequestPopup = useCallback((data) => {
    toast.custom((toastId) => (
      <div
        style={{
          background: 'linear-gradient(135deg, #FFE4E6 0%, #FDA4AF 100%)',
          border: '2px solid #E11D48',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 20px rgba(225, 29, 72, 0.35)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: '320px',
          cursor: 'pointer'
        }}
        onClick={() => { window.location.href = '/admin'; toast.dismiss(toastId); }}
      >
        <div style={{ background: '#E11D48', borderRadius: '50%', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckSquare size={20} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', color: '#881337', fontSize: '14px' }}>Demande de clôture d'assistance</div>
          <div style={{ color: '#9F1239', fontSize: '13px', marginTop: '2px' }}>{data.ref_sinistre || 'Assistance'} — {data.client_name || ''}</div>
          <div style={{ color: '#881337', fontSize: '12px', marginTop: '2px', opacity: 0.8 }}>Demandée par {data.closure_requested_by_name || 'un assisteur'}</div>
        </div>
      </div>
    ), { duration: 20000, position: 'top-right' });
  }, []);


  // ============================================================
  // POLLING PRINCIPAL
  // ============================================================
  const pollNotifications = useCallback(async () => {
    const token = getToken();
    
    if (!token || !isAdmin) return;

    try {
      const response = await fetch(
        `${backendUrl}/api/admin/notifications/poll?last_check=${encodeURIComponent(lastCheckRef.current)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        console.error('[NotificationCenter] Erreur polling:', response.status);
        return;
      }
      
      const data = await response.json();
      
      // Debug: log les données reçues
      const totalNotifs = (data.new_emails?.length || 0) + (data.read_receipts?.length || 0) + (data.chat_messages?.length || 0);
      if (totalNotifs > 0) {
        console.log('[NotificationCenter] Données reçues:', {
          new_emails: data.new_emails?.length || 0,
          read_receipts: data.read_receipts?.length || 0,
          chat_messages: data.chat_messages?.length || 0,
          server_time: data.server_time
        });
      }
      
      // Mettre à jour le timestamp pour le prochain poll
      if (data.server_time) {
        lastCheckRef.current = data.server_time;
      }
      
      // ========== TRAITEMENT : Accusés de réception ==========
      if (data.read_receipts?.length > 0) {
        for (const receipt of data.read_receipts) {
          const notifKey = `read_receipt_${receipt.id}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('read_receipt');
            showReadReceiptPopup(receipt);
          }
        }
      }
      
      // ========== TRAITEMENT : Messages Chat Admin ==========
      if (data.chat_messages?.length > 0) {
        for (const msg of data.chat_messages) {
          const notifKey = `chat_${msg.salon_id}_${msg.last_message_at}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('chat');
            showChatPopup(msg);
          }
        }
      }
      
      // ========== TRAITEMENT : Nouveaux emails ==========
      if (data.new_emails?.length > 0) {
        console.log('[NotificationCenter] Nouveaux emails détectés:', data.new_emails.length);
        for (const email of data.new_emails) {
          const notifKey = `email_${email.id}`;
          console.log('[NotificationCenter] Email:', email.subject, '- Key:', notifKey, '- Déjà notifié:', notifiedIdsRef.current.has(notifKey));
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('new_email');
            showNewEmailPopup(email);
          }
        }
      }
      
      // ========== TRAITEMENT : Hub Messages ==========
      if (data.hub_messages?.length > 0) {
        for (const msg of data.hub_messages) {
          const notifKey = `hub_${msg.id}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('chat');
            showHubMessagePopup(msg);
          }
        }
      }
      
      // ========== TRAITEMENT : Rappels de rendez-vous ==========
      if (data.appointments?.length > 0) {
        for (const apt of data.appointments) {
          const notifKey = `apt_${apt.id}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('appointment');
            showAppointmentPopup(apt);
          }
        }
      }
      
      // ========== TRAITEMENT : Rappels agenda ==========
      if (data.reminders?.length > 0) {
        for (const rem of data.reminders) {
          const notifKey = `rem_${rem.id}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('reminder');
            showReminderPopup(rem);
          }
        }
      }
      
      // ========== TRAITEMENT : Tâches à échéance ==========
      if (data.tasks?.length > 0) {
        for (const task of data.tasks) {
          const notifKey = `task_${task.id}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('reminder');
            showTaskPopup(task);
          }
        }
      }
      
      // ========== TRAITEMENT : Devis acceptés ==========
      if (data.quotes_accepted?.length > 0) {
        for (const quote of data.quotes_accepted) {
          const notifKey = `quote_${quote.id}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('appointment'); // Son festif
            showQuoteAcceptedPopup(quote);
          }
        }
      }

      // ========== TRAITEMENT : Demandes de clôture d'assistance ==========
      if (data.closure_requests?.length > 0) {
        for (const req of data.closure_requests) {
          const notifKey = `closure_${req.id}_${req.closure_requested_at || ''}`;
          if (!notifiedIdsRef.current.has(notifKey)) {
            notifiedIdsRef.current.add(notifKey);
            playSound('reminder');
            showClosureRequestPopup(req);
          }
        }
      }
      
    } catch (error) {
      console.error('[NotificationCenter] Erreur:', error);
    }
  }, [isAdmin, backendUrl, playSound, showReadReceiptPopup, showChatPopup, showNewEmailPopup, showHubMessagePopup, showAppointmentPopup, showReminderPopup, showTaskPopup, showQuoteAcceptedPopup, showClosureRequestPopup]);

  // ============================================================
  // LIFECYCLE
  // ============================================================
  useEffect(() => {
    const token = getToken();
    
    if (!isAdmin || !token) return;

    console.log('[NotificationCenter] Démarrage du polling...');

    // Premier poll après 3 secondes
    const initialTimeout = setTimeout(() => {
      pollNotifications();
    }, 3000);

    // Poll toutes les 10 secondes
    pollingRef.current = setInterval(() => {
      pollNotifications();
    }, 10000);
    
    return () => {
      console.log('[NotificationCenter] Arrêt du polling');
      clearTimeout(initialTimeout);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isAdmin, pollNotifications]);

  // Ce composant ne rend rien visuellement
  return null;
};

export default NotificationCenter;
